import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { Listings as ListingsApi, type Listing } from '../../api/client';
import { useStore } from '../../store';
import { ListingImagesManager } from './ListingImagesManager';

type Props = {
  listing: Listing | null;
  onClose: () => void;
  onSaved: () => void;
};

type EditableFields = {
  title: string;
  description: string;
  price_amount: string;  // string in the form, parsed to number on submit
  price_currency: string;
  quantity: string;
  tags: string;          // comma-separated
  materials: string;     // comma-separated
  who_made: 'i_did' | 'collective' | 'someone_else';
  when_made: 'made_to_order' | '2020_2024' | '2010_2019' | '2000_2009' | '1990s' | 'before_1990';
  is_digital: boolean;
};

function fromListing(l: Listing): EditableFields {
  // Tags / materials are stored as JSON arrays on the server; render as CSV.
  let tagsStr = '';
  let materialsStr = '';
  try { const t = JSON.parse(String(l.tags ?? '[]')); if (Array.isArray(t)) tagsStr = t.join(', '); } catch { /* keep empty */ }
  try { const m = JSON.parse(String((l as any).materials ?? '[]')); if (Array.isArray(m)) materialsStr = m.join(', '); } catch { /* keep empty */ }
  return {
    title: l.title ?? '',
    description: (l as any).description ?? '',
    price_amount: String(l.price_amount ?? ''),
    price_currency: l.price_currency ?? 'USD',
    quantity: String(l.quantity ?? 0),
    tags: tagsStr,
    materials: materialsStr,
    who_made: ((l as any).who_made ?? 'i_did') as EditableFields['who_made'],
    when_made: ((l as any).when_made ?? 'made_to_order') as EditableFields['when_made'],
    is_digital: Boolean((l as any).is_digital),
  };
}

export function ListingEditModal({ listing, onClose, onSaved }: Props) {
  const pushToast = useStore((s) => s.pushToast);
  const [form, setForm] = useState<EditableFields | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [images, setImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  useEffect(() => {
    if (listing) {
      setForm(fromListing(listing));
      setError(null);
      // Fetch full listing detail so we get images too.
      setImagesLoading(true);
      ListingsApi.get(listing.id)
        .then((detail) => setImages((detail as any).images ?? []))
        .catch(() => setImages([]))
        .finally(() => setImagesLoading(false));
    }
  }, [listing?.id]);

  if (!listing || !form) return null;

  const update = <K extends keyof EditableFields>(k: K, v: EditableFields[K]) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
  };

  const refreshImages = () => {
    if (!listing) return;
    ListingsApi.get(listing.id)
      .then((detail) => setImages((detail as any).images ?? []))
      .catch(() => setImages([]));
  };

  const handleSave = async () => {
    if (!listing) return;
    setSaving(true);
    setError(null);
    try {
      const price = parseFloat(form.price_amount);
      const quantity = parseInt(form.quantity, 10);
      if (Number.isNaN(price) || price < 0) throw new Error('Price must be a non-negative number');
      if (Number.isNaN(quantity) || quantity < 0) throw new Error('Quantity must be a non-negative integer');
      const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 13);
      const materials = form.materials.split(',').map((m) => m.trim()).filter(Boolean);
      await ListingsApi.update(listing.id, {
        title: form.title,
        description: form.description,
        price_amount: price,
        price_currency: form.price_currency,
        quantity,
        tags,
        materials,
        who_made: form.who_made,
        when_made: form.when_made,
        is_digital: form.is_digital,
      });
      pushToast({ kind: 'success', message: `Updated listing on Etsy` });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!listing}
      onClose={onClose}
      kicker="// edit"
      title={listing.title ?? 'Edit listing'}
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>
            {saving ? 'Saving…' : 'Save to Etsy'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Title" hint="Max 140 chars. Etsy uses this for search.">
          <input
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            maxLength={140}
            className="w-full"
          />
        </Field>

        <Field label="Description" hint="Tell buyers about the item, materials, dimensions.">
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            rows={4}
            className="w-full resize-none"
          />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Price" hint="Per unit">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.price_amount}
              onChange={(e) => update('price_amount', e.target.value)}
              className="w-full font-mono tabular-nums"
            />
          </Field>
          <Field label="Currency">
            <select value={form.price_currency} onChange={(e) => update('price_currency', e.target.value)} className="w-full">
              {['USD', 'EUR', 'GBP', 'BGN', 'CAD', 'AUD'].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Quantity">
            <input
              type="number"
              min="0"
              step="1"
              value={form.quantity}
              onChange={(e) => update('quantity', e.target.value)}
              className="w-full font-mono tabular-nums"
            />
          </Field>
        </div>

        <Field label="Tags" hint="Comma-separated. Up to 13 tags. Etsy uses them for search.">
          <input
            value={form.tags}
            onChange={(e) => update('tags', e.target.value)}
            placeholder="handmade, gift, custom"
            className="w-full"
          />
        </Field>

        <Field label="Materials" hint="Comma-separated. Up to 13 materials.">
          <input
            value={form.materials}
            onChange={(e) => update('materials', e.target.value)}
            placeholder="cotton, wood, recycled paper"
            className="w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Who made it">
            <select value={form.who_made} onChange={(e) => update('who_made', e.target.value as EditableFields['who_made'])} className="w-full">
              <option value="i_did">I did</option>
              <option value="collective">A member of my shop</option>
              <option value="someone_else">Another company / person</option>
            </select>
          </Field>
          <Field label="When made">
            <select value={form.when_made} onChange={(e) => update('when_made', e.target.value as EditableFields['when_made'])} className="w-full">
              <option value="made_to_order">Made to order</option>
              <option value="2020_2024">2020–2024</option>
              <option value="2010_2019">2010–2019</option>
              <option value="2000_2009">2000–2009</option>
              <option value="1990s">1990s</option>
              <option value="before_1990">Before 1990</option>
            </select>
          </Field>
        </div>

        <label className="flex items-center gap-2 text-[12.5px] text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_digital}
            onChange={(e) => update('is_digital', e.target.checked)}
            className="rounded border-cream-300"
          />
          This is a digital download (no shipping)
        </label>

        {/* Image manager — uploads, reorders, deletes hit Etsy directly via
            /api/listings/:id/images endpoints. */}
        <div className="pt-3 border-t border-cream-200">
          {imagesLoading ? (
            <div className="kicker">// images — loading…</div>
          ) : (
            <ListingImagesManager
              listingId={listing.id}
              images={images as any}
              onChanged={refreshImages}
            />
          )}
        </div>

        {error && (
          <div className="text-[12px] text-rose-500 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
